
import { tr, uiLocale } from './i18n';

export const PROPERTY_STATUS: Record<string, string> = {
  PENDING_REVIEW: tr('بانتظار المراجعة', 'Pending review'),
  AVAILABLE: tr('متاح', 'Available'),
  RESERVED: tr('محجوز', 'Reserved'),
  SOLD: tr('مباع', 'Sold'),
  RENTED: tr('مؤجر', 'Rented'),
  WITHDRAWN: tr('مسحوب', 'Withdrawn'),
  ARCHIVED: tr('مؤرشف', 'Archived'),
};

export const REQUEST_STATUS: Record<string, string> = {
  NEW: tr('جديد', 'New'),
  ASSIGNED: tr('معيّن', 'Assigned'),
  IN_PROGRESS: tr('قيد المتابعة', 'In progress'),
  CONTACTED: tr('تم التواصل', 'Contacted'),
  MATCHED: tr('تمت المطابقة', 'Matched'),
  WON: tr('ناجح', 'Won'),
  LOST: tr('خاسر', 'Lost'),
  CANCELLED: tr('ملغى', 'Cancelled'),
};

export const SOURCE: Record<string, string> = { INTERNAL: tr('داخلي', 'Internal'), PUBLIC_LINK: tr('رابط عام', 'Public link') };
export const DIRECTION: Record<string, string> = { OFFER: tr('عرض ← العقارات', 'Offer → Properties'), DEMAND: tr('طلب ← الطلبات', 'Demand → Requests') };
export const FEATURE_GROUP: Record<string, string> = { INTERIOR: tr('داخلية', 'Interior'), EXTERIOR: tr('خارجية', 'Exterior'), SERVICES: tr('خدمات', 'Services'), OTHER: tr('أخرى', 'Other') };

export const fmtDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleString(uiLocale(), { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const fmtDay = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString(uiLocale(), { dateStyle: 'medium' }) : '—');
export const fmtNum = (n: string | number | null | undefined) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString('en-US'));

// ---------------------------------------------------------------------------
// Organization app
// ---------------------------------------------------------------------------
export const EMPLOYEE_TYPE: Record<string, string> = {
  OWNER: tr('مالك', 'Owner'),
  MANAGER: tr('مدير', 'Manager'),
  EDITOR: tr('محرر', 'Editor'),
  DATA_ENTRY: tr('مدخل بيانات', 'Data entry'),
  SALES: tr('مبيعات', 'Sales'),
};

export const PERMISSION_GROUP: Record<string, string> = {
  dashboard: tr('لوحة التحكم', 'Dashboard'),
  properties: tr('العقارات', 'Properties'),
  requests: tr('الطلبات', 'Requests'),
  form: tr('النموذج', 'Form'),
  users: tr('الموظفين', 'Employees'),
  roles: tr('الأدوار', 'Roles'),
  org: tr('الإعدادات', 'Settings'),
  settings: tr('الإعدادات', 'Settings'),
  data: tr('التصدير', 'Export'),
};

/** Temporary client fallback for known Arabic-only API taxonomy values. */
const API_LABEL_EN: Record<string, string> = {
  'بيع': 'Sale',
  'شراء': 'Purchase',
  'إيجار': 'Rent',
  'استئجار': 'Rental request',
  'تأجير': 'Lease',
  'عرض': 'Offer',
  'طلب': 'Demand',
  'المالك': 'Owner',
  'مالك': 'Owner',
  'مدير': 'Manager',
  'محرر': 'Editor',
  'مدخل بيانات': 'Data entry',
  'مبيعات': 'Sales',
  'شقة': 'Apartment',
  'فيلا': 'Villa',
  'أرض': 'Land',
  'مكتب': 'Office',
  'محل': 'Shop',
  'مستودع': 'Warehouse',
  'مصنع': 'Factory',
  'سكني': 'Residential',
  'تجاري': 'Commercial',
  'صناعي': 'Industrial',
  'زراعي': 'Agricultural',
};
export const apiLabel = (value: string | null | undefined) => value ? tr(value, API_LABEL_EN[value] ?? value) : value;

export const PERMISSION_LABEL_EN: Record<string, string> = {
  'dashboard.view': 'View dashboard',
  'properties.view.own': 'View own properties',
  'properties.view.all': 'View all properties',
  'properties.create': 'Add properties',
  'properties.update.own': 'Edit own properties',
  'properties.update.all': 'Edit all properties',
  'properties.change_status': 'Change property status',
  'properties.delete': 'Delete properties',
  'requests.view.own': 'View own requests',
  'requests.view.all': 'View all requests',
  'requests.create': 'Add requests',
  'requests.update.own': 'Edit own requests',
  'requests.update.all': 'Edit all requests',
  'requests.assign': 'Assign requests',
  'requests.change_status': 'Change request status',
  'requests.delete': 'Delete requests',
  'form.submit': 'Submit the public form',
  'form.share': 'Share form links',
  'users.view': 'View employees',
  'users.manage': 'Manage employees',
  'roles.manage': 'Manage roles and permissions',
  'org.settings': 'Manage organization settings',
  'data.export': 'Export data',
};

type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
export const PROPERTY_STATUS_COLOR: Record<string, ChipColor> = {
  PENDING_REVIEW: 'warning',
  AVAILABLE: 'success',
  RESERVED: 'info',
  SOLD: 'primary',
  RENTED: 'primary',
  WITHDRAWN: 'default',
  ARCHIVED: 'default',
};
export const REQUEST_STATUS_COLOR: Record<string, ChipColor> = {
  NEW: 'warning',
  ASSIGNED: 'info',
  IN_PROGRESS: 'info',
  CONTACTED: 'primary',
  MATCHED: 'secondary',
  WON: 'success',
  LOST: 'error',
  CANCELLED: 'default',
};
export const CLOSED_REQUEST = ['WON', 'LOST', 'CANCELLED'];

export const money = (v: string | number | null | undefined, currency?: string) => (v === null || v === undefined || v === '' ? '—' : `${fmtNum(v)}${currency ? ` ${currency}` : ''}`);
export const range = (a: string | number | null | undefined, b: string | number | null | undefined, unit = '') =>
  a == null && b == null ? '—' : a != null && b != null ? `${fmtNum(a)} – ${fmtNum(b)}${unit}` : a != null ? `${tr('من', 'From')} ${fmtNum(a)}${unit}` : `${tr('حتى', 'Up to')} ${fmtNum(b)}${unit}`;
